import PropTypes, { oneOfType } from "prop-types";

import styled from "styled-components/macro";

import { useIsFullScreen } from "NavBar/stateHooks";

import FnrIcon from "UI/FnrIcon";
import { navColors } from "UI/colors";

import { headerVariants } from "./headerVariants";

/**
 * Circular icon badge used in page headers. Renders nothing when the nav collapse button is showing.
 *
 * @param {object} props
 * @param {string} props.icon - Icon name passed to FnrIcon.
 * @param {"database"|"groups"|"giving"|"signups"|"tools"} [props.variant] - Color variant (maps to navColors).
 * @param {number} [props.size=34] - Outer circle diameter in px.
 * @param {string} [props.bgColor] - Background color override (defaults to variant color or #2867ac).
 * @param {string} [props.color="white"] - Icon color.
 * @param {number|string} [props.iconSize=15] - Icon font-size in px or CSS value.
 * @param {number} [props.iconXOffset=0] - Horizontal translate offset in px.
 * @param {number} [props.iconYOffset=0] - Vertical translate offset in px.
 * @param {object} [props.styles] - Additional inline styles merged onto the icon.
 */
export default function FnrPageHeaderIcon({
  icon,
  variant,
  size = 34,
  bgColor,
  color = "white",
  iconSize = 15,
  iconXOffset = 0,
  iconYOffset = 0,
  ...additionalProps
}) {
  const { styles, ...rest } = additionalProps;
  const iconStyles = {
    fontSize: typeof iconSize === "number" ? `${iconSize}px` : iconSize,
    color: color,
    transform: `translate(${iconXOffset}px, ${iconYOffset}px)`,
    ...styles,
  };
  const bgColorToUse = bgColor || navColors[variant] || "#2867ac";

  const showingNavCollaspeButton = useIsFullScreen();

  if (showingNavCollaspeButton) return null;

  return (
    <Wrapper $bgColor={bgColorToUse} $size={size} $variant={variant}>
      <FnrIcon icon={icon} style={iconStyles} {...rest} />
    </Wrapper>
  );
}

export const fnrPageHeaderIconPropTypes = {
  icon: PropTypes.string,
  size: PropTypes.number,
  bgColor: PropTypes.string,
  color: PropTypes.string,
  iconSize: oneOfType([PropTypes.number, PropTypes.string]),
  iconXOffset: PropTypes.number,
  iconYOffset: PropTypes.number,
  styles: PropTypes.object,
};

FnrPageHeaderIcon.propTypes = {
  ...fnrPageHeaderIconPropTypes,
  icon: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(Object.values(headerVariants)),
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    ${({ $bgColor }) => $bgColor},
    ${({ $bgColor }) => $bgColor}cc
  );
`;
